import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FiInfo } from 'react-icons/fi';
import { semanticColors } from '@/common/styles/colorTokens';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  text: string;
  id: string;
  placement?: TooltipPlacement;
  children?: React.ReactNode;
  iconSize?: number;
  isOpen?: boolean; // 항상 켜져있는 상태를 제어하는 prop
}

/**
 * 재사용 가능한 Tooltip 컴포넌트
 * @param text - 툴팁에 표시될 텍스트
 * @param id - 고유한 툴팁 ID (페이지 내에서 유니크해야 함)
 * @param placement - 툴팁이 표시될 위치 (기본값: 'top')
 * @param children - 커스텀 트리거 요소 (기본값: 정보 아이콘)
 * @param iconSize - 기본 아이콘의 크기 (기본값: 12)
 */
export const Tooltip: React.FC<TooltipProps> = ({
  text,
  id,
  placement = 'top',
  isOpen,
  children,
  iconSize = 12,
}) => {
  return (
    <>
      <TooltipTrigger data-tooltip-id={id} data-tooltip-content={text} $hasChildren={!!children}>
        {children || <FiInfo size={iconSize} />}
      </TooltipTrigger>
      {createPortal(
        <ReactTooltip
          id={id}
          place={placement}
          isOpen={isOpen}
          style={{
            backgroundColor: 'black',
            color: semanticColors.text.white,
            borderRadius: '6px',
            fontSize: '0.75rem',
            maxWidth: '250px',
            padding: '8px 12px',
            zIndex: 999999,
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap',
          }}
          clickable={false}
          delayShow={isOpen ? 0 : 200}
          delayHide={isOpen ? 0 : 300}
        />,
        document.body
      )}
    </>
  );
};

const TooltipTrigger = styled.span<{ $hasChildren: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${props => !props.$hasChildren && `
    width: 16px;
    height: 16px;
    border-radius: 50%;
    color: #64748b;
    font-size: 0.75rem;
    cursor: help;
    
    &:hover {
      color: ${semanticColors.text.primary};
    }
  `}
  transition: color 0.2s ease;
`;

export default Tooltip; 